import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import api from '../lib/api';
import {
  Mail, Send, Save, Clock, Users, CheckCircle2, AlertCircle, X, Banknote,
  Eye, History, DollarSign, Calendar,
} from 'lucide-react';
import { toast } from 'sonner';

const formatCLP = (n) => `$${Math.round(n || 0).toLocaleString('es-CL')}`;

/** Lista editable de chips de email. */
function EmailListInput({ label, items, onChange, placeholder, testId = 'emails' }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim().toLowerCase();
    if (!v || items.includes(v)) { setDraft(''); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { toast.error('Email inválido'); return; }
    onChange([...items, v]);
    setDraft('');
  };
  const remove = (e) => onChange(items.filter(x => x !== e));
  return (
    <div>
      <label className="block text-xs font-medium mb-1.5 flex items-center gap-1">
        <Users className="w-3.5 h-3.5" /> {label}
      </label>
      <div className="flex gap-1 mb-1.5">
        <Input
          type="email" value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder || 'agregar@email.com'}
          className="h-8 text-xs"
          data-testid={`input-${testId}`}
        />
        <Button size="sm" className="h-8" onClick={add} data-testid={`btn-add-${testId}`}>+</Button>
      </div>
      {items.length === 0 ? (
        <div className="text-[11px] text-muted-foreground italic">sin destinatarios configurados</div>
      ) : (
        <div className="flex flex-wrap gap-1">
          {items.map(e => (
            <Badge key={e} variant="secondary" className="gap-1 pl-2 text-[11px]">
              {e}
              <button onClick={() => remove(e)} className="hover:text-red-600"><X className="w-3 h-3" /></button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

/** Form inline para editar cuenta destino de una línea factoring. */
function CuentaDestinoForm({ linea, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [data, setData] = useState(linea.cuenta_destino || {
    titular: '', rut: '', banco: 'Banco de Chile', tipo_cuenta: 'Cuenta Corriente',
    numero_cuenta: '', email_contacto: '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/flujo-caja/factoring/lineas/${linea.id}/cuenta-destino`, data);
      toast.success(`Cuenta destino de "${linea.nombre}" guardada`);
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error guardando');
    } finally {
      setSaving(false);
    }
  };

  const hasCuenta = linea.cuenta_destino && linea.cuenta_destino.numero_cuenta;

  if (!editing) {
    return (
      <div className={`rounded-md border p-3 ${hasCuenta ? 'border-emerald-300 bg-emerald-50/40 dark:bg-emerald-950/20' : 'border-amber-300 bg-amber-50/40 dark:bg-amber-950/20'}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Banknote className="w-3.5 h-3.5" />
              <span className="font-semibold text-sm">{linea.nombre}</span>
              {hasCuenta ? (
                <Badge variant="outline" className="bg-emerald-600 text-white border-0 text-[10px]">Configurada</Badge>
              ) : (
                <Badge variant="outline" className="bg-amber-500 text-white border-0 text-[10px]">Sin cuenta</Badge>
              )}
            </div>
            {hasCuenta ? (
              <div className="text-xs space-y-0.5 text-muted-foreground">
                <div><strong>{linea.cuenta_destino.titular}</strong> · RUT {linea.cuenta_destino.rut}</div>
                <div>{linea.cuenta_destino.banco} · {linea.cuenta_destino.tipo_cuenta} Nº <span className="font-mono">{linea.cuenta_destino.numero_cuenta}</span></div>
                {linea.cuenta_destino.email_contacto && <div>Contacto: {linea.cuenta_destino.email_contacto}</div>}
              </div>
            ) : (
              <div className="text-xs text-amber-700 dark:text-amber-300">
                Esta línea no tiene cuenta destino configurada. Las facturas pagadas no aparecerán en el email de solicitud.
              </div>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid={`btn-edit-cuenta-${linea.id}`}>
            {hasCuenta ? 'Editar' : 'Configurar'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-blue-300 bg-blue-50/40 dark:bg-blue-950/20 p-3 space-y-2">
      <div className="flex items-center gap-2 mb-1">
        <Banknote className="w-3.5 h-3.5" />
        <span className="font-semibold text-sm">{linea.nombre} — Configurar cuenta destino</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <label className="text-[11px] font-medium">Titular</label>
          <Input className="h-8 text-xs" value={data.titular} onChange={(e) => setData({ ...data, titular: e.target.value })} placeholder="CHIRINOSTEEL SpA" data-testid={`input-titular-${linea.id}`} />
        </div>
        <div>
          <label className="text-[11px] font-medium">RUT</label>
          <Input className="h-8 text-xs" value={data.rut} onChange={(e) => setData({ ...data, rut: e.target.value })} placeholder="76738421-1" data-testid={`input-rut-${linea.id}`} />
        </div>
        <div>
          <label className="text-[11px] font-medium">Banco</label>
          <Input className="h-8 text-xs" value={data.banco} onChange={(e) => setData({ ...data, banco: e.target.value })} placeholder="Banco de Chile" />
        </div>
        <div>
          <label className="text-[11px] font-medium">Tipo de cuenta</label>
          <Input className="h-8 text-xs" value={data.tipo_cuenta} onChange={(e) => setData({ ...data, tipo_cuenta: e.target.value })} placeholder="Cuenta Corriente" />
        </div>
        <div>
          <label className="text-[11px] font-medium">Nº Cuenta</label>
          <Input className="h-8 text-xs font-mono" value={data.numero_cuenta} onChange={(e) => setData({ ...data, numero_cuenta: e.target.value })} placeholder="9230261402" data-testid={`input-numero-${linea.id}`} />
        </div>
        <div>
          <label className="text-[11px] font-medium">Email contacto <span className="text-muted-foreground">(opcional)</span></label>
          <Input className="h-8 text-xs" type="email" value={data.email_contacto} onChange={(e) => setData({ ...data, email_contacto: e.target.value })} placeholder="contacto@ejemplo.cl" />
        </div>
      </div>
      <div className="flex gap-1.5 pt-1">
        <Button size="sm" onClick={save} disabled={saving} data-testid={`btn-save-cuenta-${linea.id}`}>
          <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Guardando…' : 'Guardar'}
        </Button>
        <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={saving}>Cancelar</Button>
      </div>
    </div>
  );
}

/** Card principal: Configuración + cuentas destino + preview + historial. */
export default function FactoringTransferSection() {
  const [cfg, setCfg] = useState(null);
  const [preview, setPreview] = useState(null);
  const [lineas, setLineas] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p, l, lg] = await Promise.all([
        api.get('/factoring-transfer/config'),
        api.get('/factoring-transfer/preview'),
        api.get('/flujo-caja/factoring/lineas?incluir_inactivas=true'),
        api.get('/factoring-transfer/logs?limit=10'),
      ]);
      setCfg(c.data);
      setPreview(p.data);
      setLineas(l.data?.items || []);
      setLogs(lg.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await api.put('/factoring-transfer/config', cfg);
      setCfg(r.data.config);
      toast.success('Configuración guardada');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async () => {
    if (!cfg.to || cfg.to.length === 0) { toast.error('Agrega destinatarios primero'); return; }
    setSending(true);
    try {
      const r = await api.post('/factoring-transfer/enviar-ahora');
      const d = r.data;
      if (d.sent) {
        toast.success(`Email enviado a ${d.to.length} destinatario(s) · ${d.count} transf · ${formatCLP(d.total)}`);
      } else {
        toast.info(`No enviado: ${d.reason || d.error || 'sin items'}`);
      }
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally {
      setSending(false);
    }
  };

  if (loading || !cfg) {
    return <Card><CardContent className="p-4"><Skeleton className="h-40 w-full" /></CardContent></Card>;
  }

  const transferTotalPreview = (preview?.transferencias || []).reduce((a, t) => a + t.monto, 0);
  const otrosTotalPreview = (preview?.otros_pagos || []).reduce((a, t) => a + t.monto, 0);

  return (
    <>
      <Card data-testid="card-factoring-transfer">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-600" />
                Solicitud Transferencias Factoring
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Email diario a tesorería con listado de transferencias a ejecutar desde Bchile 1 hacia las cuentas de cada línea factorizada.
                Se dispara al marcar facturas como pagadas + un cron diario.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{cfg.enabled ? 'Activo' : 'Pausado'}</span>
              <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="switch-ftr-enabled" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Hora + días hábiles */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1.5 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> Hora envío diario
              </label>
              <Input type="time" value={cfg.hora} onChange={(e) => setCfg({ ...cfg, hora: e.target.value })} data-testid="input-ftr-hora" />
            </div>
            <div className="flex items-center gap-2 pt-5">
              <Switch checked={cfg.solo_dias_habiles} onCheckedChange={(v) => setCfg({ ...cfg, solo_dias_habiles: v })} data-testid="switch-ftr-habiles" />
              <label className="text-xs">Solo días hábiles (lun-vie)</label>
            </div>
          </div>

          {/* Destinatarios */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <EmailListInput label="Para (To)" items={cfg.to} onChange={(v) => setCfg({ ...cfg, to: v })} placeholder="tesoreria@chirinosteel.com" testId="ftr-to" />
            <EmailListInput label="En copia (Cc)" items={cfg.cc || []} onChange={(v) => setCfg({ ...cfg, cc: v })} placeholder="gerencia@chirinosteel.com" testId="ftr-cc" />
          </div>

          <div className="flex gap-2 pt-1">
            <Button onClick={handleSave} disabled={saving} data-testid="btn-save-ftr">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Guardando…' : 'Guardar configuración'}
            </Button>
            <Button variant="secondary" onClick={handleSendNow} disabled={sending} data-testid="btn-send-ftr-now">
              <Send className="w-4 h-4 mr-1.5" /> {sending ? 'Enviando…' : 'Enviar ahora (preview + envío)'}
            </Button>
          </div>

          {/* Cuentas destino por línea */}
          <div className="pt-3 border-t">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
              <Banknote className="w-4 h-4" /> Cuentas destino por línea de factoring
            </h3>
            <p className="text-[11px] text-muted-foreground mb-2">
              Cada línea debe tener una cuenta destino configurada para poder aparecer en el email.
            </p>
            <div className="space-y-2">
              {lineas.length === 0 ? (
                <div className="text-xs text-muted-foreground italic p-2">No hay líneas de factoring.</div>
              ) : lineas.map(l => (
                <CuentaDestinoForm key={l.id} linea={l} onSaved={loadAll} />
              ))}
            </div>
          </div>

          {/* Preview: lo que se va a enviar hoy */}
          <div className="pt-3 border-t">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
              <Eye className="w-4 h-4" /> Preview del próximo envío — {preview?.fecha || 'hoy'}
            </h3>
            {(preview?.transferencias || []).length === 0 && (preview?.otros_pagos || []).length === 0 ? (
              <div className="text-xs text-muted-foreground italic p-3 bg-slate-50 dark:bg-slate-900/40 rounded-md border">
                No hay pagos del día aún. Este email no se enviará (a menos que marques "Enviar ahora" manual).
              </div>
            ) : (
              <div className="space-y-2">
                <div className="rounded-md border overflow-hidden">
                  <div className="bg-emerald-50 dark:bg-emerald-950/30 px-3 py-1.5 text-xs font-semibold flex justify-between">
                    <span>📤 Transferencias a ejecutar ({(preview.transferencias || []).length})</span>
                    <span className="tabular-nums">{formatCLP(transferTotalPreview)}</span>
                  </div>
                  {(preview.transferencias || []).length === 0 ? (
                    <div className="px-3 py-2 text-xs text-muted-foreground italic">Sin transferencias pendientes hoy.</div>
                  ) : (
                    <table className="w-full text-xs">
                      <tbody>
                        {preview.transferencias.map((t) => (
                          <tr key={t.asig_id} className="border-t">
                            <td className="px-3 py-1 font-mono">{t.folio}</td>
                            <td className="px-3 py-1 truncate max-w-[180px]">{t.cliente}</td>
                            <td className="px-3 py-1 text-muted-foreground">→ {t.linea_nombre}</td>
                            <td className="px-3 py-1 text-right font-semibold tabular-nums">{formatCLP(t.monto)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {(preview.lineas_sin_cuenta || []).length > 0 && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-2 text-xs text-amber-800 dark:text-amber-300">
                    ⚠️ <strong>Líneas sin cuenta destino:</strong> {preview.lineas_sin_cuenta.join(', ')}. Las facturas de estas líneas <u>NO</u> se incluirán en el email hasta configurar su cuenta.
                  </div>
                )}

                {(preview.otros_pagos || []).length > 0 && (
                  <div className="rounded-md border overflow-hidden">
                    <div className="bg-slate-100 dark:bg-slate-900/50 px-3 py-1.5 text-xs font-semibold flex justify-between">
                      <span>📥 Otros pagos recibidos (CxC directa, no factoring)</span>
                      <span className="tabular-nums">{formatCLP(otrosTotalPreview)}</span>
                    </div>
                    <div className="px-3 py-1.5 text-[11px] text-muted-foreground">
                      Se mencionan informativamente — ya están en Bchile 1, no requieren transferencia.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Historial */}
      {logs.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base"><History className="w-4 h-4" /> Historial envíos transferencias</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {logs.map((h, idx) => (
                <div key={idx} className="px-4 py-2 flex items-center justify-between text-xs gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {h.success ? <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />}
                    <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                    <span>{new Date(h.timestamp).toLocaleString('es-CL')}</span>
                    <Badge variant="outline" className="text-[10px]">{h.manual ? 'manual' : 'auto'}</Badge>
                  </div>
                  <div className="text-muted-foreground text-right">
                    {h.transferencias_count} transf · <span className="tabular-nums">{formatCLP(h.total_transferir)}</span>
                    {h.error && <div className="text-red-600 mt-0.5">{h.error}</div>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}
